import React, { useState } from "react";
import { Loader2, Zap, SlidersHorizontal, Check } from "lucide-react";
import { toast } from "sonner";

const GOALS = [
  { value: "performance", label: "Perform", hint: "Fuel the training" },
  { value: "fat-loss", label: "Lean up", hint: "Controlled fat loss" },
  { value: "maintenance", label: "Maintain", hint: "Hold steady" },
  { value: "recovery", label: "Recover", hint: "Injury or rest block" },
];
const SPORTS = [
  { value: "run", label: "Run" }, { value: "cycle", label: "Bike" },
  { value: "swim", label: "Swim" }, { value: "triathlon", label: "Tri" },
  { value: "strength", label: "Gym" }, { value: "team", label: "Team sport" },
];
const AVOID = [
  { key: "vegetarian", label: "Vegetarian", pattern: true },
  { key: "vegan", label: "Vegan", pattern: true },
  { key: "gluten", label: "No gluten" },
  { key: "dairy", label: "No dairy" },
  { key: "nuts", label: "No nuts" },
  { key: "pork", label: "No pork" },
];
// 2-5 days land on the days most people actually train.
const DAY_SPREAD = { 1: [2], 2: [1, 4], 3: [1, 3, 5], 4: [1, 3, 5, 6], 5: [0, 1, 3, 4, 6], 6: [0, 1, 2, 3, 4, 6], 7: [0, 1, 2, 3, 4, 5, 6] };

const field = "w-full bg-black border border-white/15 focus:border-[#E2E30A] outline-none px-3 py-3 text-white text-base";
const chip = (on) => `px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider border transition-colors ${on ? "border-[#E2E30A] bg-[#E2E30A] text-black" : "border-white/15 text-zinc-300 hover:border-white/40"}`;

// Express build — the shortest honest route to a plan. Asks only what we can't
// infer, fills everything else with coaching defaults, then generates. The full
// 9-step wizard stays one tap away for anyone who wants the detail.
export default function ExpressBuild({ plan, setPlan, onBuilt, onFullWizard }) {
  const p = plan.profile || {};
  const [f, setF] = useState({
    sex: p.sex || "male",
    age: p.age || "",
    weight: p.weight || "",
    height: p.height || "",
    goal: plan.goal || "performance",
    sport: "run",
    days: 4,
    mins: 60,
    avoid: [],
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const build = async () => {
    if (!f.age || !f.weight || !f.height) {
      toast.error("Age, weight and height are all we really need — pop those in.");
      return;
    }
    setBusy(true);
    const patterns = AVOID.filter((a) => a.pattern && f.avoid.includes(a.key));
    const dietPattern = f.avoid.includes("vegan") ? "vegan"
      : f.avoid.includes("vegetarian") ? "vegetarian" : "omnivore";
    const exclusions = f.avoid.filter((k) => !patterns.some((x) => x.key === k));
    const trainDays = DAY_SPREAD[f.days] || DAY_SPREAD[4];
    const days = Array.from({ length: 7 }, (_, i) => ({
      sessions: trainDays.includes(i)
        ? [{ sport: f.sport, duration: Number(f.mins) || 60, intensity: "endurance",
             timing: "AM", terrain: "flat", heat: false, key: false, mealStyle: "3-meals" }]
        : [],
    }));
    setPlan((prev) => ({
      ...prev,
      goal: f.goal,
      mealPlanning: true,
      profile: {
        ...prev.profile,
        sex: f.sex, age: String(f.age), weight: Number(f.weight), height: Number(f.height),
        activity: prev.profile?.activity || "active",
        usage: "full",
      },
      dietary: { ...prev.dietary, pattern: dietPattern, exclusions },
      trainingWeek: { ...prev.trainingWeek, days },
      currentStep: 6,
    }));
    // The wizard regenerates on input change; give that a beat, then land them
    // on their plan.
    setTimeout(() => { setBusy(false); onBuilt?.(); }, 1200);
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="express-build">
      <div className="flex items-center gap-2.5 mb-2">
        <span className="grid place-items-center h-9 w-9 bg-[#E2E30A] text-black shrink-0">
          <Zap className="h-5 w-5" />
        </span>
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#E2E30A] font-bold">Quick build · about a minute</p>
      </div>
      <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight leading-tight">
        Six answers and your week is done
      </h1>
      <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
        We'll fill in everything else with sensible coaching defaults — and you can change any
        of it afterwards. Nothing here is locked in.
      </p>

      <div className="mt-8 space-y-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold mb-2">You</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <select data-testid="express-sex" value={f.sex} onChange={(e) => set("sex", e.target.value)} className={field}>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
            <input data-testid="express-age" value={f.age} onChange={(e) => set("age", e.target.value)}
              inputMode="numeric" placeholder="Age" className={field} />
            <input data-testid="express-weight" value={f.weight} onChange={(e) => set("weight", e.target.value)}
              inputMode="decimal" placeholder="Weight kg" className={field} />
            <input data-testid="express-height" value={f.height} onChange={(e) => set("height", e.target.value)}
              inputMode="numeric" placeholder="Height cm" className={field} />
          </div>
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold mb-2">What's the goal</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {GOALS.map((g) => (
              <button key={g.value} type="button" data-testid={`express-goal-${g.value}`}
                onClick={() => set("goal", g.value)} className={`${chip(f.goal === g.value)} text-left`}>
                <span className="block">{g.label}</span>
                <span className={`block text-[9px] normal-case tracking-normal mt-0.5 ${f.goal === g.value ? "text-black/70" : "text-zinc-500"}`}>{g.hint}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold mb-2">Main sport</p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {SPORTS.map((s) => (
              <button key={s.value} type="button" data-testid={`express-sport-${s.value}`}
                onClick={() => set("sport", s.value)} className={chip(f.sport === s.value)}>{s.label}</button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold mb-2">
              Training days a week · <span className="text-[#E2E30A]">{f.days}</span>
            </p>
            <input type="range" min="1" max="7" value={f.days} data-testid="express-days"
              onChange={(e) => set("days", Number(e.target.value))} className="w-full accent-[#E2E30A]" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold mb-2">
              Typical session · <span className="text-[#E2E30A]">{f.mins} min</span>
            </p>
            <input type="range" min="20" max="180" step="10" value={f.mins} data-testid="express-mins"
              onChange={(e) => set("mins", Number(e.target.value))} className="w-full accent-[#E2E30A]" />
          </div>
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold mb-2">Anything you don't eat</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {AVOID.map((a) => {
              const on = f.avoid.includes(a.key);
              return (
                <button key={a.key} type="button" data-testid={`express-avoid-${a.key}`}
                  onClick={() => set("avoid", on ? f.avoid.filter((k) => k !== a.key) : [...f.avoid, a.key])}
                  className={`${chip(on)} inline-flex items-center justify-center gap-1.5`}>
                  {on && <Check className="h-3.5 w-3.5" />} {a.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <button type="button" data-testid="express-build-btn" onClick={build} disabled={busy}
        className="mt-8 w-full h-14 bg-[#E2E30A] hover:bg-white disabled:opacity-70 text-black font-bold uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 transition-colors">
        {busy ? <><Loader2 className="h-5 w-5 animate-spin" /> Building your week…</> : <><Zap className="h-5 w-5" /> Build my week</>}
      </button>

      <button type="button" data-testid="express-full-wizard" onClick={onFullWizard} disabled={busy}
        className="mt-3 w-full py-3 border border-white/15 hover:border-white/40 text-zinc-300 hover:text-white text-[11px] uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-colors">
        <SlidersHorizontal className="h-4 w-4" /> I'd rather set every detail myself
      </button>
      <p className="text-[10px] text-zinc-500 mt-3 text-center">
        Defaults used: {f.days} training days, {f.mins}-minute sessions, flexible eating, auto macros.
        Change anything later from Build / Edit.
      </p>
    </div>
  );
}
